/**
 * Key/value storage that degrades instead of throwing.
 *
 * `window.localStorage` is not always usable, and every failure mode is silent:
 *
 *  - Safari in private mode throws on `setItem` once the quota is zero.
 *  - A sandboxed iframe throws on mere access.
 *  - Node 26 exposes an experimental `localStorage` global that returns `undefined`
 *    unless the process was started with `--localstorage-file`, which shadows jsdom's
 *    inside vitest.
 *
 * Catching those exceptions and quietly doing nothing would mean the session token is
 * never stored — the login would appear to succeed and the next request would be
 * anonymous. So the backend is probed once at import, and falls back to process memory:
 * the operator stays signed in for the life of the tab rather than not at all.
 */

export interface KeyValueStore {
  /** False when values will not survive a reload. Surfaced for diagnostics. */
  readonly persistent: boolean
  get(key: string): string | null
  set(key: string, value: string): void
  remove(key: string): void
}

export function createMemoryStore(): KeyValueStore {
  const entries = new Map<string, string>()
  return {
    persistent: false,
    get: (key) => entries.get(key) ?? null,
    set: (key, value) => void entries.set(key, value),
    remove: (key) => void entries.delete(key),
  }
}

/**
 * A store backed by `globalThis.localStorage`, or null if it cannot be written to.
 *
 * The write/read/remove probe matters: in Safari private mode `localStorage` exists and
 * reads fine, and only fails on write — so "does the object exist" is not the question.
 */
export function createLocalStore(): KeyValueStore | null {
  try {
    const backing = globalThis.localStorage
    if (!backing) return null

    const probe = '__lcrm_probe__'
    backing.setItem(probe, '1')
    const echoed = backing.getItem(probe)
    backing.removeItem(probe)
    if (echoed !== '1') return null

    return {
      persistent: true,
      get: (key) => backing.getItem(key),
      set: (key, value) => backing.setItem(key, value),
      remove: (key) => backing.removeItem(key),
    }
  } catch {
    return null
  }
}

export function resolveStore(): KeyValueStore {
  return createLocalStore() ?? createMemoryStore()
}

/** The store the app uses. Resolved once, at import. */
export const storage: KeyValueStore = resolveStore()
